import { useState, useRef, useEffect } from "react";
import * as api from "../api/client";
import type { ApiCtx } from "../api/client";

interface ChatProps {
  ctx: ApiCtx;
  defaultModel?: string;
  isAdmin?: boolean;
}

export function Chat({ ctx, defaultModel, isAdmin = false }: ChatProps) {
  const [chatMessages, setChatMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(defaultModel || "");
  const [showInfo, setShowInfo] = useState<any>(null);
  const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  useEffect(() => {
    if (defaultModel && !selectedModel) {
      setSelectedModel(defaultModel);
    }
  }, [defaultModel]);

  const handleSendChat = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMsg = chatInput;
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setChatLoading(true);

    try {
      const res = await api.chat(ctx, userMsg, chatMessages, selectedModel);
      if (res.code === 0) {
        setChatMessages((prev) => [...prev, { role: "assistant", content: res.response }]);
      } else {
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Fehler: ${res.msg || "Antwort konnte nicht geladen werden."}` }
        ]);
      }
    } catch (err: any) {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Netzwerkfehler: ${err.message || err}` }
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const allModels = ((window as any).config?.models || [
    {
      "ID": "gpt-5.6-terra:low",
      "label": "GPT-5.6 Terra (low effort)",
      "description": "Guter Allrounder für alltägliche Dokumentaufgaben wie Zusammenfassen, Umformatieren oder einfaches Heraussuchen von Informationen. Schnell und günstig, aber bei Aufgaben, die viel Abwägen oder das Zusammenführen mehrerer Dokumente erfordern, stößt es an Grenzen.",
      "provider": "OpenAI, USA",
      "cost": "mittel",
      "advanced": false
    },
    {
      "ID": "gpt-5.6-terra:medium",
      "label": "GPT-5.6 Terra (medium effort)",
      "description": "Solider Allrounder für die meisten agentischen Dokumentaufgaben – zuverlässig, geduldig bei mehrstufigen Abläufen und gut im Umgang mit Werkzeugen. Für besonders lange oder verschachtelte Aufgaben mit vielen Dokumenten ist es nicht ganz so stark wie die teureren Modelle.",
      "provider": "OpenAI, USA",
      "cost": "mittel",
      "advanced": false
    },
    {
      "ID": "gpt-5.6-luna:low",
      "label": "GPT-5.6 Luna (low effort)",
      "description": "Ideal für einfache, häufig wiederkehrende Aufgaben wie kurze Auszüge oder das grobe Einsortieren von Dokumenten. Sehr günstig und flott, aber ungeeignet für komplexere Aufgaben, bei denen mehrere Schritte durchdacht werden müssen.",
      "provider": "OpenAI, USA",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "gpt-5.6-sol:medium",
      "label": "GPT-5.6 Sol (medium effort)",
      "description": "Die richtige Wahl für anspruchsvolle, langwierige Aufgaben, bei denen viele Dokumente gegeneinander abgeglichen oder komplexe Zusammenhänge erkannt werden müssen. Sehr gründlich und verlässlich, dafür spürbar teurer als die anderen Varianten der Familie.",
      "provider": "OpenAI, USA",
      "cost": "hoch",
      "advanced": false
    },
    {
      "ID": "openai.gpt-oss-120b-1:0",
      "label": "GPT-OSS 120B",
      "description": "Gut geeignet, wenn Dokumente selbst gehostet oder besonders kostengünstig verarbeitet werden sollen. Löst viele agentische Aufgaben ordentlich, kann aber keine Bilder oder Layouts in Dokumenten erkennen und ist bei sehr komplexen Aufgabenketten weniger zuverlässig als teurere Modelle.",
      "provider": "OpenAI, USA",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "openai.gpt-oss-20b-1:0",
      "label": "GPT-OSS 20B",
      "description": "Sehr sparsame Wahl für einfache, private oder lokal laufende Dokumentaufgaben. Für anspruchsvollere, mehrstufige Aufgaben oder Dokumente mit Bildern reicht die Leistung nicht aus.",
      "provider": "OpenAI, USA",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "mistral.pixtral-large-2502-v1:0",
      "label": "Pixtral Large (25.02)",
      "description": "Gute Wahl, wenn Dokumente neben Text auch Tabellen, Diagramme oder Bilder enthalten, die verstanden werden müssen. Wird von Mistral nicht mehr aktiv weiterentwickelt, daher für neue, langfristige Projekte weniger empfehlenswert als aktuelle Modelle.",
      "provider": "Mistral AI, Frankreich",
      "cost": "mittel",
      "advanced": false
    },
    {
      "ID": "minimax.minimax-m2.5",
      "label": "MiniMax M2.5",
      "description": "Sehr gut geeignet, um aus Dokumenten heraus strukturierte Office-Ergebnisse (z. B. Word, Excel, PowerPoint) zu erstellen, und dabei erstaunlich günstig. Bei sehr speziellen Fachfragen etwas schwächer als andere Modelle.",
      "provider": "MiniMax, China",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "nvidia.nemotron-super-3-120b",
      "label": "NVIDIA Nemotron 3 Super 120B",
      "description": "Gut geeignet für Aufgaben mit sehr langen oder vielen Dokumenten, die im Blick behalten werden müssen. Günstig und schnell, allerdings weniger sprachlich vielseitig und weniger erprobt als etablierte Alternativen.",
      "provider": "NVIDIA, USA",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "zai.glm-4.7-flash",
      "label": "GLM-4.7-Flash",
      "description": "Eine der günstigsten Optionen für einfache bis mittlere Dokument- und Rechercheaufgaben, with ordentlicher Zuverlässigkeit beim Werkzeugeinsatz. Für sehr lange oder komplexe Aufgaben mit vielen Dokumenten weniger geeignet.",
      "provider": "Zhipu AI (Z.ai), China",
      "cost": "niedrig",
      "advanced": false
    },
    {
      "ID": "anthropic.claude-fable-5",
      "label": "Claude Fable 5",
      "description": "Die beste Wahl für sehr anspruchsvolle, lang laufende Aufgaben, bei denen selbstständig geplant, geprüft und über viele Schritte hinweg gearbeitet werden muss. Dafür deutlich teurer, und bei heiklen Themen (z. B. Sicherheits- oder Biologiebezug) kann es Anfragen ablehnen oder an ein anderes Modell weiterreichen.",
      "provider": "Anthropic, USA",
      "cost": "hoch",
      "advanced": false
    },
    {
      "ID": "anthropic.claude-opus-4-6-v1",
      "label": "Claude Opus 4.6",
      "description": "Sehr fähig bei der Analyse und Erstellung anspruchsvoller Dokumente sowie bei längeren, selbstständigen Arbeitsabläufen. Es gibt inzwischen ein neueres, noch leistungsfähigeres Anthropic-Modell, das für neue Projekte oft die bessere Wahl ist.",
      "provider": "Anthropic, USA",
      "cost": "hoch",
      "advanced": false
    },
    {
      "ID": "anthropic.claude-sonnet-4-6",
      "label": "Claude Sonnet 4.6",
      "description": "Guter, ausgewogener Allrounder für alltägliche Dokument- und Rechercheaufgaben zu einem moderaten Preis. Bei sehr langen oder komplex verschachtelten Aufgaben mit vielen Dokumenten ist es den teureren Anthropic-Modellen unterlegen.",
      "provider": "Anthropic, USA",
      "cost": "mittel",
      "advanced": false
    }
  ]).filter((m: any) => isAdmin || !m.advanced);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <style>{`
        @keyframes rightpane-spin {
          to { transform: rotate(360deg); }
        }
        .rightpane-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid var(--c-primary);
          border-top-color: transparent;
          border-radius: 50%;
          animation: rightpane-spin 0.8s linear infinite;
          display: inline-block;
        }
      `}</style>
      
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "12px",
        fontSize: "0.85rem",
        color: "var(--c-text-sec)"
      }}>
        <span>Modell:</span>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <select
            className="input"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={chatLoading || chatMessages.length > 0}
            style={{
              padding: "2px 6px",
              fontSize: "0.85rem",
              width: "auto",
              maxWidth: "240px",
              height: "auto"
            }}
          >
            {selectedModel && !allModels.some((m: any) => m.ID === selectedModel) && (
              <option value={selectedModel}>{selectedModel}</option>
            )}
            {allModels.map((m: any) => (
              <option key={m.ID} value={m.ID}>
                {m.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn ghost"
            style={{
              padding: "2px 6px",
              fontSize: "0.85rem",
              minWidth: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1
            }}
            onClick={() => {
              const currentModel = allModels.find((m: any) => m.ID === selectedModel);
              if (currentModel) {
                setShowInfo(currentModel);
              }
            }}
            title="Modell-Details anzeigen"
          >
            ?
          </button>
        </div>
      </div>

      <div style={{
        flex: 1,
        overflowY: "auto",
        marginBottom: "12px",
        paddingRight: "4px",
        display: "flex",
        flexDirection: "column",
        gap: "10px"
      }}>
        {chatMessages.length === 0 ? (
          <div style={{
            color: "var(--c-text-sec)",
            textAlign: "center",
            marginTop: "40px",
            fontSize: "0.9rem",
            padding: "0 10px"
          }}>
            Stelle Fragen zum Arbeitsbereich. Der Chatbot kann deine Dokumente lesen, bearbeiten und Suchen durchführen.
          </div>
        ) : (
          chatMessages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                backgroundColor: msg.role === "user" ? "var(--c-primary)" : "var(--bg)",
                color: msg.role === "user" ? "#000000" : "var(--c-text)",
                padding: "10px 14px",
                borderRadius: "12px",
                borderBottomRightRadius: msg.role === "user" ? "2px" : "12px",
                borderBottomLeftRadius: msg.role === "assistant" ? "2px" : "12px",
                fontSize: "0.92rem",
                lineHeight: "1.4",
                whiteSpace: "pre-wrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
              }}
            >
              {msg.content}
            </div>
          ))
        )}
        {chatLoading && (
          <div style={{
            alignSelf: "flex-start",
            backgroundColor: "var(--bg)",
            color: "var(--c-text-sec)",
            padding: "10px 14px",
            borderRadius: "12px",
            borderBottomLeftRadius: "2px",
            fontSize: "0.92rem",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            <span className="rightpane-spinner"></span>
            Denkt nach...
          </div>
        )}
        <div ref={chatMessagesEndRef} />
      </div>
      
      <form onSubmit={handleSendChat} style={{
        display: "flex",
        gap: "8px",
        borderTop: "1px solid var(--line)",
        paddingTop: "12px"
      }}>
        <input
          type="text"
          className="input"
          placeholder="Frage stellen..."
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          disabled={chatLoading}
          style={{ flex: 1, fontSize: "0.92rem" }}
        />
        <button
          type="submit"
          className="btn primary"
          disabled={chatLoading || !chatInput.trim()}
          style={{ padding: "6px 12px", fontSize: "0.92rem" }}
        >
          Senden
        </button>
        {chatMessages.length > 0 && (
          <button
            type="button"
            className="btn"
            onClick={() => setChatMessages([])}
            disabled={chatLoading}
            title="Chat leeren"
            style={{ padding: "6px 10px", fontSize: "14px" }}
          >
            🗑
          </button>
        )}
      </form>

      {showInfo && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
          padding: "16px"
        }} onClick={() => setShowInfo(null)}>
          <div style={{
            backgroundColor: "var(--surface)",
            color: "var(--c-text)",
            border: "1px solid var(--line)",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "480px",
            padding: "24px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, fontSize: "1.15rem" }}>{showInfo.label}</h3>
              <button type="button" className="btn ghost" style={{ minWidth: 0, padding: "4px 8px" }} onClick={() => setShowInfo(null)}>✕</button>
            </div>
            <div style={{ fontSize: "0.95rem", lineHeight: "1.5", marginBottom: "16px", whiteSpace: "pre-wrap" }}>
              {showInfo.description}
            </div>
            <div style={{ fontSize: "0.85rem", color: "var(--muted)", borderTop: "1px solid var(--line)", paddingTop: "12px" }}>
              <div><strong>Anbieter (Vendor):</strong> {showInfo.provider}</div>
              <div style={{ marginTop: "4px" }}><strong>Kosten (Cost):</strong> {showInfo.cost}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
